/**
 * Authentication Service
 * Handles token management and authentication flow using cookies
 */

import Cookies from 'js-cookie';
import { CONFIG } from '../config';
import { AuthChangeListener, Branch, User, Permissions } from '@/types';

class AuthService {
  private tokenKey = CONFIG.AUTH.tokenKey;
  private userKey = 'userData';
  private permissionsKey = 'permissions';
  private branchIdKey = 'branchId';
  private branchesKey = 'branches';
  private cookieOptions = {
    expires: 14, // 14 days
    secure: CONFIG.ENV.isProduction, // Only use secure cookies in production
    sameSite: 'strict' as const,
  };
  private authChangeListeners: AuthChangeListener[] = [];

  /**
   * Get stored authentication token from cookie
   */
  getToken(): string | null {
    try {
      return Cookies.get(this.tokenKey) || null;
    } catch (error) {
      console.error('Failed to get token from cookie:', error);
      return null;
    }
  }

  /**
   * Store authentication token in cookie
   */
  setToken(token: string): void {
    try {
      Cookies.set(this.tokenKey, token, this.cookieOptions);
    } catch (error) {
      console.error('Failed to store token in cookie:', error);
    }
  }

  /**
   * Store user data in cookie
   */
  setUser(user: User): void {
    try {
      Cookies.set(this.userKey, JSON.stringify(user), this.cookieOptions);
    } catch (error) {
      console.error('Failed to store user data in cookie:', error);
    }
  }

  /**
   * Get stored user data from cookie
   */
  getUser(): User | null {
    try {
      const userData = Cookies.get(this.userKey);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to get user data from cookie:', error);
      return null;
    }
  }

  /**
   * Remove authentication token from cookie
   */
  removeToken(): void {
    try {
      Cookies.remove(this.tokenKey);
    } catch (error) {
      console.error('Failed to remove token from cookie:', error);
    }
  }

  /**
   * Remove user data from cookie
   */
  removeUser(): void {
    try {
      Cookies.remove(this.userKey);
    } catch (error) {
      console.error('Failed to remove user data from cookie:', error);
    }
  }

  /**
   * Store permissions in cookie
   */
  setPermissions(permissions: Permissions): void {
    try {
      Cookies.set(this.permissionsKey, JSON.stringify(permissions), this.cookieOptions);
    } catch (error) {
      console.error('Failed to store permissions in cookie:', error);
    }
  }

  /**
   * Get stored permissions from cookie
   */
  getPermissions(): Permissions | null {
    try {
      const permissionsData = Cookies.get(this.permissionsKey);
      return permissionsData ? JSON.parse(permissionsData) : null;
    } catch (error) {
      console.error('Failed to get permissions from cookie:', error);
      return null;
    }
  }

  /**
   * Remove permissions from cookie
   */
  removePermissions(): void {
    try {
      Cookies.remove(this.permissionsKey);
    } catch (error) {
      console.error('Failed to remove permissions from cookie:', error);
    }
  }

  /**
   * Store branch ID in cookie
   */
  setBranchId(branchId: string): void {
    try {
      Cookies.set(this.branchIdKey, branchId, this.cookieOptions);
    } catch (error) {
      console.error('Failed to store branch ID in cookie:', error);
    }
  }

  /**
   * Get stored branch ID from cookie
   */
  getBranchId(): string | null {
    try {
      return Cookies.get(this.branchIdKey) || null;
    } catch (error) {
      console.error('Failed to get branch ID from cookie:', error);
      return null;
    }
  }

  /**
   * Remove branch ID from cookie
   */
  removeBranchId(): void {
    try {
      Cookies.remove(this.branchIdKey);
    } catch (error) {
      console.error('Failed to remove branch ID from cookie:', error);
    }
  }

  /**
   * Store all branches in cookie (for future branch selector UI)
   */
  setBranches(branches: Branch[]): void {
    try {
      Cookies.set(this.branchesKey, JSON.stringify(branches), this.cookieOptions);
    } catch (error) {
      console.error('Failed to store branches in cookie:', error);
    }
  }

  /**
   * Get all stored branches from cookie
   */
  getBranches(): Branch[] {
    try {
      const branchesData = Cookies.get(this.branchesKey);
      return branchesData ? JSON.parse(branchesData) : [];
    } catch (error) {
      console.error('Failed to get branches from cookie:', error);
      return [];
    }
  }

  /**
   * Remove all branches from cookie
   */
  removeBranches(): void {
    try {
      Cookies.remove(this.branchesKey);
    } catch (error) {
      console.error('Failed to remove branches from cookie:', error);
    }
  }

  /**
   * Switch to a different branch (for future branch selector)
   */
  switchBranch(branchId: string): void {
    const branches = this.getBranches();
    const branch = branches.find(b => b.id === branchId);
    
    if (branch) {
      this.setBranchId(branchId);
      console.log('🏢 Switched to branch:', branchId, `(${branch.name})`);
    } else {
      console.error('Branch not found:', branchId);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  /**
   * Get stored token (backend will validate)
   */
  getValidToken(): string | null {
    return this.getToken();
  }

  /**
   * Handle unauthenticated state
   */
  handleUnauthenticated(): void {
    console.log('🔐 Handling unauthenticated state - clearing auth data and redirecting');
    this.removeToken();
    this.removeUser();
    this.removePermissions();
    this.removeBranchId();
    this.removeBranches();
    this.notifyAuthChange(false);
    
    // Redirect to login
    if (typeof window !== 'undefined') {
      console.log('🔐 Redirecting to login page');
      // Use replace to avoid back button issues
      window.location.replace('/login');
    } else {
      console.warn('🔐 Window object not available for redirect');
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    this.removeToken();
    this.removeUser();
    this.removePermissions();
    this.removeBranchId();
    this.removeBranches();
    this.notifyAuthChange(false);
    
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  }

  /**
   * Add authentication change listener
   */
  addAuthChangeListener(listener: AuthChangeListener): void {
    this.authChangeListeners.push(listener);
  }

  /**
   * Remove authentication change listener
   */
  removeAuthChangeListener(listener: AuthChangeListener): void {
    this.authChangeListeners = this.authChangeListeners.filter(l => l !== listener);
  }

  /**
   * Notify all listeners of authentication state change
   */
  private notifyAuthChange(isAuthenticated: boolean): void {
    this.authChangeListeners.forEach(listener => {
      try {
        listener(isAuthenticated);
      } catch (error) {
        console.error('Error in auth change listener:', error);
      }
    });
  }

  /**
   * Set authentication data and notify listeners
   */
  setAuthData(token: string, user: User, branches?: Branch[], permissions?: Permissions): void {
    this.setToken(token);
    this.setUser(user);
    
    // Store permissions if provided
    if (permissions) {
      this.setPermissions(permissions);
    }
    
    // Store all branches and set the first branch as default
    if (branches && branches.length > 0) {
      this.setBranches(branches);
      this.setBranchId(branches[0].id);
      console.log('🏢 Default branch set:', branches[0].id, `(${branches[0].name})`);
      console.log('🏢 Total branches available:', branches.length);
    }
    
    this.notifyAuthChange(true);
  }

  /**
   * Check if token exists (backend will validate)
   */
  validateCurrentToken(): boolean {
    const token = this.getValidToken();
    return !!token;
  }
}

export const authService = new AuthService();
